/**
 * Simple geolocation utility for detecting Germany IP addresses
 */
class GeolocationService {
  private static instance: GeolocationService;
  private isGermany: boolean | null = null;
  private countryCode: string | null = null;
  private isLoading: boolean = false;
  private loadPromise: Promise<void> | null = null;

  private constructor() {}

  static getInstance(): GeolocationService {
    if (!GeolocationService.instance) {
      GeolocationService.instance = new GeolocationService();
    }
    return GeolocationService.instance;
  }

  /**
   * Detects if the user is from Germany based on their IP
   */
  async detectGermany(): Promise<boolean> {
    if (this.isGermany !== null) {
      return this.isGermany;
    }

    if (this.isLoading && this.loadPromise) {
      await this.loadPromise;
      return this.isGermany || false;
    }

    this.isLoading = true;
    this.loadPromise = this.fetchGeolocation();

    try {
      await this.loadPromise;
    } catch (error) {
      console.warn(
        'Failed to detect geolocation, defaulting to non-German region:',
        error
      );
      this.isGermany = false;
    } finally {
      this.isLoading = false;
      this.loadPromise = null;
    }

    return this.isGermany || false;
  }

  private async fetchGeolocation(): Promise<void> {
    try {
      // Try ipapi.co first (free service)
      const response = await fetch('https://ipapi.co/json/', {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        const countryCode = (
          data.country_code ||
          data.country ||
          ''
        ).toUpperCase();
        this.countryCode = countryCode;
        this.isGermany = countryCode === 'DE';
        return;
      }
    } catch (error) {
      console.warn('Primary geolocation service failed:', error);
    }

    try {
      // Fallback to ipinfo.io
      const response = await fetch('https://ipinfo.io/json', {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        const countryCode = (data.country || '').toUpperCase();
        this.countryCode = countryCode;
        this.isGermany = countryCode === 'DE';
        return;
      }
    } catch (error) {
      console.warn('Fallback geolocation service failed:', error);
    }

    // Default to non-German if all methods fail
    this.isGermany = false;
  }

  /**
   * Synchronous check if Germany has been detected (only works after detectGermany() has been called)
   */
  isGermanySync(): boolean {
    return this.isGermany === true;
  }

  /**
   * Get the detected country code (only works after detectGermany() has been called)
   */
  getCountryCode(): string | null {
    return this.countryCode;
  }

  /**
   * Reset the detection state (useful for testing)
   */
  reset(): void {
    this.isGermany = null;
    this.countryCode = null;
    this.isLoading = false;
    this.loadPromise = null;
  }
}

export const geolocationService = GeolocationService.getInstance();
